import React, { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { Modal, Button, Input, Select } from "./ui";
import { formatMoney } from "../lib/utils";
import {
  ASSET_KINDS, LIABILITY_TYPES, METALS, CURRENCIES, CURRENCY_LABELS, lbl,
} from "../lib/assets";
import { Check, ArrowRight, ArrowLeft, Plus, Trash2 } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);
const num = (v) => (v === "" || v == null ? null : Number(v));

function Field({ label, hint, children, optional }) {
  const { t } = useApp();
  return (
    <div>
      <label className="text-xs font-medium text-text-secondary block mb-1">
        {label}{optional && <span className="text-text-secondary/60"> {t("optional")}</span>}
      </label>
      {children}
      {hint && <p className="text-[11px] text-text-secondary mt-1">{hint}</p>}
    </div>
  );
}

function CurrencySelect({ value, onChange, testid }) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {CURRENCIES.map((c) => <option key={c} value={c}>{CURRENCY_LABELS[c]}</option>)}
    </Select>
  );
}

export default function AssetWizard({ entity, item, onClose, onSaved }) {
  const { t, lang, toast } = useApp();
  const editing = !!item;
  const defaultCur = entity.country === "US" ? "USD" : "EUR";

  // step 1 = type picker, step 2 = details
  const initialKind = editing
    ? ASSET_KINDS.find((k) => (item.kind === "liability" ? k.value === "liability" : k.value === item.asset_type))
    : null;
  const [step, setStep] = useState(editing ? 2 : 1);
  const [picked, setPicked] = useState(initialKind || null);
  const [busy, setBusy] = useState(false);
  const [metalPrices, setMetalPrices] = useState({});

  const [form, setForm] = useState({
    name: item?.name || "",
    currency: item?.currency || defaultCur,
    value: item?.value ?? "",
    value_date: item?.value_date || todayISO(),
    cost: item?.cost ?? "",
    cost_date: item?.cost_date || "",
    notes: item?.notes || "",
    liability_type: item?.liability_type || "mortgage",
    metal: item?.metal || "gold",
    quantity: item?.quantity ?? "",
    metal_cost_per_oz: (item?.cost != null && item?.quantity) ? Number((item.cost / item.quantity).toFixed(2)) : "",
    link_mortgage: false,
    m_balance: "", m_original: "", m_orig_date: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [valuations, setValuations] = useState(
    (item?.valuations || []).map((v) => ({ date: v.date, value: v.value })));
  const addValuation = () => setValuations((vs) => [...vs, { date: todayISO(), value: "" }]);
  const setVal = (i, k, v) => setValuations((vs) => vs.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const removeVal = (i) => setValuations((vs) => vs.filter((_, idx) => idx !== i));

  const isLiability = picked?.kind === "liability";
  const type = picked?.value;

  // live metal spot prices (in the chosen currency)
  useEffect(() => {
    if (type !== "precious_metal") return;
    let active = true;
    api.get(`/portfolio/metals?currency=${form.currency}`).then(({ data }) => {
      if (active) setMetalPrices(Object.fromEntries(data.map((m) => [m.metal, m.price])));
    }).catch(() => {});
    return () => { active = false; };
  }, [type, form.currency]);

  const metalSpot = metalPrices[form.metal];
  const metalValue = metalSpot != null && form.quantity ? metalSpot * Number(form.quantity) : null;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        entity_id: entity.id,
        kind: isLiability ? "liability" : "asset",
        name: form.name.trim() || (isLiability ? t("assets_liability") : t("assets_asset")),
        currency: form.currency,
        value_date: form.value_date || null,
        cost: num(form.cost),
        cost_date: form.cost_date || null,
        notes: form.notes || null,
      };
      if (isLiability) {
        payload.liability_type = form.liability_type;
        payload.value = num(form.value);
        payload.valuations = valuations.filter((v) => v.date).map((v) => ({ date: v.date, value: num(v.value) }));
      } else if (type === "precious_metal") {
        payload.asset_type = "precious_metal";
        payload.metal = form.metal;
        payload.quantity = num(form.quantity);
        const perOz = num(form.metal_cost_per_oz);
        payload.cost = (perOz && num(form.quantity)) ? Number((perOz * num(form.quantity)).toFixed(2)) : null;
      } else {
        payload.asset_type = type;
        payload.value = num(form.value);
        payload.valuations = valuations.filter((v) => v.date).map((v) => ({ date: v.date, value: num(v.value) }));
        if (type === "property" && form.link_mortgage && num(form.m_balance)) {
          payload.mortgage = {
            balance: num(form.m_balance),
            original_amount: num(form.m_original),
            origination_date: form.m_orig_date || null,
            currency: form.currency,
            balance_date: form.value_date || null,
          };
        }
      }
      if (editing) await api.put(`/portfolio/assets/${item.id}`, payload);
      else await api.post("/portfolio/assets", payload);
      toast(editing ? t("assets_saved") : t("assets_created"));
      onSaved();
    } finally { setBusy(false); }
  };

  // ---- step 1: type picker ----
  if (step === 1) {
    return (
      <Modal open onClose={onClose} title={t("assets_add_title")}
        footer={
          <Button onClick={() => setStep(2)} disabled={!picked} data-testid="asset-wizard-continue">
            {t("continue")} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        }>
        <p className="text-sm text-text-secondary mb-4">{t("assets_pick_type")}</p>
        <div className="grid grid-cols-2 gap-3">
          {ASSET_KINDS.map((k) => {
            const Ico = k.icon;
            const active = picked?.value === k.value;
            return (
              <button key={k.value} onClick={() => setPicked(k)} data-testid={`asset-type-${k.value}`}
                className="relative text-left rounded-xl border p-4 transition-all hover:bg-surface-hover"
                style={active ? { borderColor: k.color, background: `${k.color}14` } : {}}>
                {active && (
                  <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-text-primary text-background flex items-center justify-center">
                    <Check className="w-3 h-3" />
                  </span>
                )}
                <span className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                  style={{ background: `${k.color}1f` }}>
                  <Ico className="w-5 h-5" style={{ color: k.color }} />
                </span>
                <div className="font-heading font-semibold">{lbl(k.label, lang)}</div>
                <div className="text-[11px] text-text-secondary">{lbl(k.sub, lang)}</div>
              </button>
            );
          })}
        </div>
      </Modal>
    );
  }

  // ---- step 2: details ----
  const title = isLiability ? t("assets_liability") : lbl(picked.label, lang);
  return (
    <Modal open onClose={onClose} title={title}
      footer={<>
        {!editing && (
          <Button variant="outline" onClick={() => setStep(1)} data-testid="asset-wizard-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> {t("back")}
          </Button>
        )}
        <Button onClick={save} disabled={busy} data-testid="asset-save">
          {busy ? t("loading") : isLiability ? t("assets_add_liability") : (editing ? t("save") : t("assets_create_asset"))}
        </Button>
      </>}>
      <p className="text-sm text-text-secondary mb-4">
        {isLiability ? t("assets_liability_sub") : t("assets_details_sub")}
      </p>
      <div className="space-y-4">
        {isLiability && (
          <Field label={t("assets_liability_type")}>
            <Select value={form.liability_type} onChange={(e) => set("liability_type", e.target.value)} data-testid="asset-liability-type">
              {LIABILITY_TYPES.map((o) => <option key={o.value} value={o.value}>{lbl(o.label, lang)}</option>)}
            </Select>
          </Field>
        )}

        <Field label={t("assets_name")}>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)}
            placeholder={isLiability ? t("assets_name_ph_liability") : t("assets_name_ph_asset")} data-testid="asset-name-input" />
        </Field>

        <Field label={t("assets_currency")}>
          <CurrencySelect value={form.currency} onChange={(v) => set("currency", v)} testid="asset-currency" />
        </Field>

        {type === "precious_metal" ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("assets_metal")}>
                <Select value={form.metal} onChange={(e) => set("metal", e.target.value)} data-testid="asset-metal">
                  {METALS.map((m) => <option key={m.value} value={m.value}>{lbl(m.label, lang)}</option>)}
                </Select>
              </Field>
              <Field label={t("assets_quantity_oz")}>
                <Input type="number" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} placeholder="0.00" data-testid="asset-quantity-input" />
              </Field>
            </div>
            <div className="rounded-lg border px-3 py-2 text-sm flex items-center justify-between" data-testid="asset-metal-spot">
              <span className="text-text-secondary">
                {t("assets_spot")} · {metalSpot != null ? formatMoney(metalSpot, form.currency, { decimals: 2 }) : "—"} / oz
              </span>
              <span className="font-semibold tabular-nums">
                {metalValue != null ? formatMoney(metalValue, form.currency, { decimals: 2 }) : "—"}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field optional label={t("assets_metal_cost_oz")}>
                <Input type="number" value={form.metal_cost_per_oz} onChange={(e) => set("metal_cost_per_oz", e.target.value)} placeholder="0.00" data-testid="asset-metal-cost-input" />
              </Field>
              <Field label={t("assets_value_date")}>
                <Input type="date" value={form.value_date} onChange={(e) => set("value_date", e.target.value)} data-testid="asset-value-date" />
              </Field>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label={isLiability ? t("assets_current_balance") : t("assets_current_value")}>
                <Input type="number" value={form.value} onChange={(e) => set("value", e.target.value)} placeholder="0.00" data-testid="asset-value-input" />
              </Field>
              <Field label={isLiability ? t("assets_balance_date") : t("assets_value_date")}>
                <Input type="date" value={form.value_date} onChange={(e) => set("value_date", e.target.value)} data-testid="asset-value-date" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field optional label={isLiability ? t("assets_original_amount") : t("assets_purchase_price")}>
                <Input type="number" value={form.cost} onChange={(e) => set("cost", e.target.value)} placeholder="0.00" data-testid="asset-cost-input" />
              </Field>
              <Field optional label={isLiability ? t("assets_origination_date") : t("assets_purchase_date")}>
                <Input type="date" value={form.cost_date} onChange={(e) => set("cost_date", e.target.value)} data-testid="asset-cost-date" />
              </Field>
            </div>
            <p className="text-[11px] text-text-secondary -mt-2">
              {isLiability ? t("assets_track_paydown") : t("assets_track_gain")}
            </p>

            {/* valuation history — manual items move over time in the chart */}
            <div className="rounded-lg border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium text-text-secondary">{t("assets_valuation_history")}</div>
                <button type="button" onClick={addValuation} data-testid="asset-add-valuation"
                  className="text-xs text-text-secondary hover:text-text-primary inline-flex items-center gap-1">
                  <Plus className="w-3.5 h-3.5" /> {t("assets_add_valuation")}
                </button>
              </div>
              {valuations.length === 0 ? (
                <p className="text-[11px] text-text-secondary">{t("assets_valuation_hint")}</p>
              ) : (
                <div className="space-y-2" data-testid="asset-valuations">
                  {valuations.map((v, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`asset-valuation-${i}`}>
                      <Input type="date" value={v.date} onChange={(e) => setVal(i, "date", e.target.value)} className="flex-1" data-testid={`asset-valuation-date-${i}`} />
                      <Input type="number" value={v.value} onChange={(e) => setVal(i, "value", e.target.value)} placeholder="0.00" className="flex-1" data-testid={`asset-valuation-value-${i}`} />
                      <button type="button" onClick={() => removeVal(i)} data-testid={`asset-valuation-remove-${i}`} className="text-text-secondary hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* property → create + link a mortgage */}
        {type === "property" && !editing && (
          <div className="rounded-lg border p-3 space-y-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="asset-link-mortgage">
              <input type="checkbox" checked={form.link_mortgage} onChange={(e) => set("link_mortgage", e.target.checked)} />
              {t("assets_link_mortgage")}
            </label>
            {form.link_mortgage && (
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("assets_current_balance")}>
                  <Input type="number" value={form.m_balance} onChange={(e) => set("m_balance", e.target.value)} placeholder="0.00" data-testid="asset-mortgage-balance" />
                </Field>
                <Field optional label={t("assets_original_amount")}>
                  <Input type="number" value={form.m_original} onChange={(e) => set("m_original", e.target.value)} placeholder="0.00" data-testid="asset-mortgage-original" />
                </Field>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
